import type { ReactNode } from 'react';
import { cx } from '../cx';

export type TimelineEvent = {
  id: string;
  /** Formatted date (formatDate). */
  date: ReactNode;
  /** Machine-readable date for <time>, e.g. "2026-10-11". */
  dateTime?: string;
  title: ReactNode;
  body?: ReactNode;
  /** Where the event comes from ("Trade register", "Seen on their careers page"). Required. */
  source: ReactNode;
};

export type TimelineProps = {
  /** Shown newest first: sorted by dateTime when every event has one, else in the order given. Events without a source are left out. */
  events: TimelineEvent[];
  className?: string;
};

/** Events with a source, newest first when they all carry a dateTime. Pure, exported for tests. */
export function orderTimeline(events: TimelineEvent[]): TimelineEvent[] {
  const sourced = events.filter((e) => e.source != null && e.source !== '' && e.source !== false);
  if (sourced.every((e) => e.dateTime))
    return [...sourced].sort((a, b) => (b.dateTime! < a.dateTime! ? -1 : b.dateTime! > a.dateTime! ? 1 : 0));
  return sourced;
}

/** Dated events in order, each with its basis: a company's history, an application's history. */
export function Timeline({ events: input, className }: TimelineProps) {
  const events = orderTimeline(input);
  return (
    <ol className={cx('relative flex flex-col gap-6 border-l-2 border-jc-border pl-6', className)}>
      {events.map((event, i) => (
        <li key={event.id} className="relative flex flex-col gap-1">
          <span
            aria-hidden="true"
            className={cx(
              'absolute -left-8 top-1.5 size-3.5 rounded-full ring-4 ring-jc-page',
              i === 0 ? 'bg-jc-ink' : 'bg-jc-border-control',
            )}
          />
          <time dateTime={event.dateTime} className="type-eyebrow text-jc-ink-secondary">
            {event.date}
          </time>
          <span className="type-subheading text-jc-ink">{event.title}</span>
          {event.body ? <p className="text-sm text-jc-ink-secondary">{event.body}</p> : null}
          <span className="text-xs text-jc-ink-secondary">{event.source}</span>
        </li>
      ))}
    </ol>
  );
}
